import {
  socketService,
  PlayerStats,
  GameRoom,
} from '../services/SocketService';

export class LobbyScreen {
  private container: HTMLElement;
  private currentPlayer: PlayerStats | null = null;
  private availableGames: GameRoom[] = [];
  private leaderboard: PlayerStats[] = [];
  private onCreateGame: (gameName: string) => void;
  private onJoinGame: (gameId: string) => void;
  private onLogout: () => void;

  constructor(
    container: HTMLElement,
    callbacks: {
      onCreateGame: (gameName: string) => void;
      onJoinGame: (gameId: string) => void;
      onLogout: () => void;
    }
  ) {
    this.container = container;
    this.onCreateGame = callbacks.onCreateGame;
    this.onJoinGame = callbacks.onJoinGame;
    this.onLogout = callbacks.onLogout;
  }

  public setCurrentPlayer(player: PlayerStats | null): void {
    this.currentPlayer = player;
  }

  public setAvailableGames(games: GameRoom[]): void {
    this.availableGames = games;
    this.renderGameSessions();
  }

  public setLeaderboard(leaderboard: PlayerStats[]): void {
    this.leaderboard = leaderboard;
    this.renderLeaderboard();
  }

  public render(): void {
    this.container.innerHTML = `
      <div class="lobby-container">
  <!-- Left Panel: Auth, Player Info & Rules -->
  <div class="lobby-left-panel">
          <div class="game-title">
            <h1>🎮 Caro</h1>
            <p class="subtitle">Online Multiplayer</p>
          </div>
 
          <div id="auth-section" class="auth-section">
            <div class="auth-tabs">
              <button class="auth-tab active" data-tab="login">Login</button>
              <button class="auth-tab" data-tab="register">New Player</button>
            </div>
 
            <div id="login-form" class="auth-form active">
              <input type="text" id="login-username" placeholder="Enter your Gametag" />
              <button id="login-btn" class="primary-btn">Login</button>
            </div>
 
            <div id="register-form" class="auth-form">
              <input type="text" id="register-username" placeholder="Choose your Gametag" />
              <button id="register-btn" class="primary-btn">Create Gametag</button>
              <p class="info-text">Creating new gametag will invalidate previous gametag </p>
            </div>
 
            <div id="error-message" class="error-message"></div>
          </div>
 
          <div id="player-section" class="player-section hidden">
            <div class="player-card">
              <div class="player-info">
                <h1 id="player-name"></h1>
                <div class="player-stats">
                  <div class="stat">
                    <span class="stat-label">ELO</span>
                    <span class="stat-value" id="player-elo"></span>
                  </div>
                  <div class="stat">
                    <span class="stat-label">Rank</span>
                    <span class="stat-value" id="player-rank"></span>
                  </div>
                  <div class="stat">
                    <span class="stat-label">W/L/D</span>
                    <span class="stat-value" id="player-record"></span>
                  </div>
                </div>
              </div>
            </div>
 
            <div class="game-actions">
              <!-- Create game form hidden/removed for challenge-only lobby -->
            </div>
 
            <div class="game-rules-section left-rules">
            <h3>📋 Game Rules</h3>
            <ul class="rules-list">
              <li>Get 5 in a row to win</li>
              <li>Must have at least one open end</li>
              <li>30×30 board</li>
              <li>Ranked matches with ELO system</li>
              <li>Chess clock: 5 minutes per player</li>
            </ul>
          </div>
 
            <button id="logout-btn" class="secondary-btn logout-btn-bottom">Logout</button>
          </div>
 
         
        </div>
 
        <!-- Middle Panel: Leaderboard Only -->
        <div class="lobby-middle-panel">
          <div class="welcome-header">
            <h2>Welcome to the Temple of Legendary</h2>
            <p>Start playing to enter!</p>
          </div>
          <div class="lobby-content">
            <div id="leaderboard-section-middle" class="leaderboard-section-middle">
              <h3>🏆 Top 10 Leaderboard</h3>
              <div id="leaderboard-list-middle" class="leaderboard-list"></div>
            </div>
          </div>
        </div>
 
        <!-- Right Panel: Online Players -->
        <div class="lobby-right-panel hidden" id="lobby-right-panel">
          <h3>Online Players</h3>
          <button id="refresh-games-btn" class="secondary-btn">🔄 Refresh</button>
          <div id="online-players" class="online-players"></div>
        </div>
      </div>
    `;

    this.attachEventListeners();
    this.renderLeaderboard();

    // Hide right panel initially if no player is logged in
    if (!this.currentPlayer) {
      this.hideRightPanel();
    }
  }

  private attachEventListeners(): void {
    // Auth tabs
    const authTabs = this.container.querySelectorAll('.auth-tab');
    authTabs.forEach((tab) => {
      tab.addEventListener('click', (e) => {
        const target = e.target as HTMLElement;
        const tabName = target.dataset.tab;

        authTabs.forEach((t) => t.classList.remove('active'));
        target.classList.add('active');

        const loginForm = this.container.querySelector('#login-form');
        const registerForm = this.container.querySelector('#register-form');

        if (tabName === 'login') {
          loginForm?.classList.add('active');
          registerForm?.classList.remove('active');
        } else {
          loginForm?.classList.remove('active');
          registerForm?.classList.add('active');
        }
      });
    });

    // Login
    this.container
      .querySelector('#login-btn')
      ?.addEventListener('click', () => this.handleLogin());

    // Register
    this.container
      .querySelector('#register-btn')
      ?.addEventListener('click', () => this.handleRegister());

    // Logout
    this.container
      .querySelector('#logout-btn')
      ?.addEventListener('click', () => this.onLogout());

    // Create game form removed in challenge flow - no handler attached

    // Refresh online players
    this.container
      .querySelector('#refresh-games-btn')
      ?.addEventListener('click', () => {
        socketService.getOnlinePlayers();
      });
  }

  private async handleLogin(): Promise<void> {
    const username = (
      this.container.querySelector('#login-username') as HTMLInputElement
    ).value.trim();

    if (!username) {
      this.showError('Please enter your gametag');
      return;
    }

    try {
      const player = await socketService.login(username);
      this.currentPlayer = player;

      // Save gametag to localStorage
      localStorage.setItem('gomoku_gametag', username);

      this.showPlayerSection();
      this.showError('');
      socketService.getLeaderboard(10);
      // Refresh online players immediately after login so the right panel is up-to-date
      socketService.getOnlinePlayers();
      // Notify outer UI that login occurred (useful to start polling)
      window.dispatchEvent(
        new CustomEvent('gomoku:login-success', { detail: { username } })
      );
    } catch (error) {
      this.showError(error instanceof Error ? error.message : 'Login failed');
    }
  }

  private async handleRegister(): Promise<void> {
    const username = (
      this.container.querySelector('#register-username') as HTMLInputElement
    ).value.trim();

    if (!username) {
      this.showError('Please enter a gametag');
      return;
    }

    if (username.length < 3) {
      this.showError('Gametag must be at least 3 characters');
      return;
    }

    try {
      const player = await socketService.register(username);
      this.currentPlayer = player;

      // Save gametag to localStorage
      localStorage.setItem('gomoku_gametag', username);

      this.showPlayerSection();
      this.showError('');
      socketService.getLeaderboard(10);
      // Refresh online players immediately after registration/login
      socketService.getOnlinePlayers();
      // Notify outer UI that login occurred (useful to start polling)
      window.dispatchEvent(
        new CustomEvent('gomoku:login-success', { detail: { username } })
      );
    } catch (error) {
      this.showError(
        error instanceof Error ? error.message : 'Registration failed'
      );
    }
  }

  private handleCreateGame(): void {
    if (!this.currentPlayer) return;

    // Auto-generate game name: PlayerName_Timestamp
    const timestamp = Date.now();
    const gameName = `${this.currentPlayer.username}_${timestamp}`;
    this.onCreateGame(gameName);
  }

  public showPlayerSection(): void {
    const authSection = this.container.querySelector('#auth-section');
    const playerSection = this.container.querySelector('#player-section');

    authSection?.classList.add('hidden');
    playerSection?.classList.remove('hidden');

    if (this.currentPlayer) {
      (
        this.container.querySelector('#player-name') as HTMLElement
      ).textContent = this.currentPlayer.username;
      (this.container.querySelector('#player-elo') as HTMLElement).textContent =
        this.currentPlayer.elo.toString();
      (
        this.container.querySelector('#player-rank') as HTMLElement
      ).textContent = `#${this.currentPlayer.rank || '?'}`;
      (
        this.container.querySelector('#player-record') as HTMLElement
      ).textContent = `${this.currentPlayer.wins}/${this.currentPlayer.losses}/${this.currentPlayer.draws}`;
    }

    // Show right panel when logged in
    this.showRightPanel();
  }

  public showAuthSection(): void {
    const authSection = this.container.querySelector('#auth-section');
    const playerSection = this.container.querySelector('#player-section');

    authSection?.classList.remove('hidden');
    playerSection?.classList.add('hidden');

    // Hide right panel when logged out
    this.hideRightPanel();
  }

  private showRightPanel(): void {
    const rightPanel = this.container.querySelector('#lobby-right-panel');
    rightPanel?.classList.remove('hidden');
  }

  private hideRightPanel(): void {
    const rightPanel = this.container.querySelector('#lobby-right-panel');
    rightPanel?.classList.add('hidden');
  }

  private renderGameSessions(): void {
    const container = this.container.querySelector('#game-sessions');
    if (!container) return;

    if (this.availableGames.length === 0) {
      container.innerHTML =
        '<p class="no-games">No games available. Create one!</p>';
      return;
    }

    container.innerHTML = this.availableGames
      .map(
        (game) => `
      <div class="game-card" data-game-id="${game.id}">
        <div class="game-info">
          <h4>${game.name}</h4>
        </div>
        <button class="join-btn" data-game-id="${game.id}">Join</button>
      </div>
    `
      )
      .join('');

    // Attach join button listeners
    container.querySelectorAll('.join-btn').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        const gameId = (e.target as HTMLElement).dataset.gameId;
        if (gameId) this.onJoinGame(gameId);
      });
    });
  }

  private renderLeaderboard(): void {
    if (this.leaderboard.length === 0) {
      const middleLeaderboard = this.container.querySelector(
        '#leaderboard-list-middle'
      );
      if (middleLeaderboard) {
        middleLeaderboard.innerHTML = '<p class="no-data">No players yet</p>';
      }
      return;
    }

    const leaderboardHTML = this.leaderboard
      .map(
        (player, index) => `
      <div class="leaderboard-item">
        <span class="rank">#${index + 1}</span>
        <span class="player-name">${player.username}</span>
        <span class="player-elo">${player.elo}</span>
      </div>
    `
      )
      .join('');

    const middleLeaderboard = this.container.querySelector(
      '#leaderboard-list-middle'
    );
    if (middleLeaderboard) {
      middleLeaderboard.innerHTML = leaderboardHTML;
    }
  }

  public showMessage(message: string): void {
    // You can add a toast/notification system here
    console.info(message);
  }

  public showError(message: string): void {
    const errorEl = this.container.querySelector('#error-message');
    if (errorEl) {
      errorEl.textContent = message;
    }
  }

  public getCurrentPlayer(): PlayerStats | null {
    return this.currentPlayer;
  }

  /**
   * Show an in-app prompt to accept/decline an incoming challenge.
   * Returns a promise resolved with true=accepted, false=declined.
   */
  public promptChallenge(requesterName: string): Promise<boolean> {
    return new Promise((resolve) => {
      // Create overlay container
      const overlay = document.createElement('div');
      overlay.className = 'challenge-overlay';
      overlay.innerHTML = `
        <div class="challenge-box">
          <p><strong>${requesterName}</strong> challenged you to a match.</p>
          <div class="challenge-actions">
            <button class="accept-btn primary-btn">Accept</button>
            <button class="decline-btn secondary-btn">Decline</button>
          </div>
        </div>
      `;

      // Basic styles (kept inline so no CSS edits required)
      overlay.setAttribute(
        'style',
        `position:fixed;top:0;left:0;right:0;bottom:0;display:flex;align-items:center;justify-content:center;z-index:10000;background:rgba(0,0,0,0.45);`
      );
      const box = overlay.querySelector('.challenge-box') as HTMLElement;
      if (box)
        box.setAttribute(
          'style',
          'background:#fff;padding:18px;border-radius:8px;max-width:320px;text-align:center;box-shadow:0 6px 20px rgba(0,0,0,0.25);'
        );

      const acceptBtn = overlay.querySelector(
        '.accept-btn'
      ) as HTMLButtonElement;
      const declineBtn = overlay.querySelector(
        '.decline-btn'
      ) as HTMLButtonElement;

      const cleanup = (result: boolean) => {
        try {
          document.body.removeChild(overlay);
        } catch (e) {
          /* ignore */
        }
        resolve(result);
      };

      // Attach handlers after append (buttons may be null until in DOM)
      document.body.appendChild(overlay);

      const acceptEl = overlay.querySelector(
        '.accept-btn'
      ) as HTMLButtonElement;
      const declineEl = overlay.querySelector(
        '.decline-btn'
      ) as HTMLButtonElement;

      if (acceptEl) acceptEl.addEventListener('click', () => cleanup(true));
      if (declineEl) declineEl.addEventListener('click', () => cleanup(false));

      // If the user clicks outside the box, treat as decline
      overlay.addEventListener('click', (ev) => {
        if (ev.target === overlay) cleanup(false);
      });
    });
  }
}
